import {
  AloftArea,
  AloftPrice,
  ORDER_FEATURES,
  getCraftsmanCost,
  isUndefined,
} from '@aloft/toolkit-shared';
import { CraftsmanCostProps } from '@aloft/toolkit-shared/lib/craftsman';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';

const calculateWorkshopCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const identification = propertyIdentification[id];

  if (!improvementsForTransaction || !identification) return undefined;

  const { workshop } = improvementsForTransaction;
  const {
    address: { PostalCode: zipCode },
  } = identification;

  if (!workshop || !workshop.area || isUndefined(workshop.quality) || isUndefined(zipCode)) {
    return undefined;
  }

  const calculator = getCraftsmanCost(costProps);

  return calculator.getWorkshopCost({
    hasFeature: ORDER_FEATURES.workshop.hasFeature(improvementsForTransaction),
    sqft: AloftArea.schema.parse(workshop.area),
    quality: workshop.quality,
    zipCode,
  });
};

const getTotalOrBreakdownWorkshopCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
  type: 'totalCost' | 'zipOnly',
) => {
  const costOutput = calculateWorkshopCost(costProps, improvements, propertyIdentification, id);
  if (!costOutput) return AloftPrice.zero;

  return type === 'totalCost' ? costOutput.totalCost : costOutput.multiplierBreakdown[type];
};

const getTotalWorkshopCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) =>
  getTotalOrBreakdownWorkshopCost(costProps, improvements, propertyIdentification, id, 'totalCost');

export const selectWorkshopCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  getTotalWorkshopCost,
);

export const selectZipOnlyWorkshopCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  (costProps, improvements, propertyIdentification, id) =>
    getTotalOrBreakdownWorkshopCost(costProps, improvements, propertyIdentification, id, 'zipOnly'),
);

export const selectWorkshopCosts = createSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectCompSequence,
  ],
  (costProps, improvements, propertyIdentification, compSequence) =>
    ['subject', ...compSequence].reduce((acc: Record<string, AloftPrice>, propID) => {
      const cost = getTotalWorkshopCost(costProps, improvements, propertyIdentification, propID);
      if (cost) acc[propID] = cost;
      return acc;
    }, {}),
);

export const selectWorkshopCostDisplay = createSelector([selectWorkshopCost], (workshopCost) =>
  workshopCost?.toString(true),
);
